import { AxiosResponse } from 'axios';
import { OriginFilter } from '../../models/origin-filter.model';
import Track from '../../models/track.model';
import { Candidate, CandidateProcess } from './../../models/candidate.model';
import { SelectedCandidate } from './../../models/selected-candidate.model';
import { CandidateQuery } from './models/candidates-query.model';
import PaginatedResponse from './models/paginated-response.model';
import talentApi from './talent-api-config';
import { RecruiterCommentInterface } from '../../models/recruiter-comment.model';

class EventApi {

    async fetchEventData(eventSlug: string): Promise<AxiosResponse<Event>> {
        return talentApi.get<Event>(`/talent/${eventSlug}/`);
    }

    async fetchSelectedCandidates(eventSlug: string, query?: CandidateQuery): Promise<AxiosResponse<PaginatedResponse<SelectedCandidate>>> {
        const page = query?.selectedCandidatesPage ? parseInt(query?.selectedCandidatesPage) : 1;
        const params = { ...query, page_size: 10, page };
        
        if (params.status === 'my_candidates') {
            delete params.status;
            params.my = 'true';
        }

        const response = await talentApi.get<PaginatedResponse<SelectedCandidate>>(`/talent/${eventSlug}/selecteds/`, { params });
        response.data.current_page = page;
        response.data.page_size = params.page_size;

        if (response.data?.results) {
            for (let i = 0; i < response.data?.results.length; i++) {
                const selectedCandidate = response.data?.results[i];
                if (!selectedCandidate.source) {
                    selectedCandidate.candidate.is_pool = true;
                }
            }
        }

        return response;
    }

    async fetchOriginFilter(eventSlug: string): Promise<AxiosResponse<OriginFilter[]>> {
        return talentApi.get<OriginFilter[]>(`/talent/${eventSlug}/origin-filter/`);
    }

    async fetchEventTracks(eventSlug: string): Promise<AxiosResponse<Track[]>> {
        return talentApi.get<Track[]>(`/talent/${eventSlug}/tracks/`);
    }

    async fetchEventTrack(trackUuid: string): Promise<AxiosResponse<Track>> {
        return talentApi.get<Track>(`/talent/track/${trackUuid}/`);
    }

    async fetchTrackCandidates(trackUuid: string, query?: CandidateQuery): Promise<AxiosResponse<PaginatedResponse<Candidate>>> {
        const page = query?.trackingPage ? parseInt(query?.trackingPage) : 1;
        const page_size = query?.pageSize || 10;
        const params = { ...query, page_size: page_size, pageSize: undefined, page };
        const response = await talentApi.get<PaginatedResponse<Candidate>>(`/talent/track/${trackUuid}/candidates/`, { params });
        response.data.current_page = page;
        response.data.page_size = page_size;
        return response;
    }

    async fetchCandidateProcess(trackUiid: string): Promise<AxiosResponse<CandidateProcess>> {
        return talentApi.get<CandidateProcess>(`/talent/track/${trackUiid}/candidates/selected/`);
    }

    async fetchTrackSkills(trackUuid: string): Promise<AxiosResponse<string[]>> {
        return talentApi.get<string[]>(`/talent/track/${trackUuid}/skills/`);
    }

    async fetchRecruiterComments(candidateId: string): Promise<AxiosResponse<RecruiterCommentInterface[]>> {
        return await talentApi.get<RecruiterCommentInterface[]>(`/talent/comment/candidate/${candidateId}`);
    }

    async updateRecruiterResponsible(actionId: string, responsibleUuid: string): Promise<AxiosResponse<RecruiterCommentInterface[]>> {
        return await talentApi.put<RecruiterCommentInterface[]>(`/talent/action/${actionId}/responsible/`, {
            responsible_uuid: responsibleUuid
        });
    }

    async saveRecruiterComment(candidateId: string, comment: string) {
        interface DataInterface {
            comment: string;
        }

        const data: DataInterface = {
            comment: comment
        }

        return await talentApi.post(`/talent/comment/candidate/${candidateId}/`, data);
    }

    async updateRecruiterComment(comment: string, commentId: string) {
        const data = {
            comment: comment
        }
        return await talentApi.put(`/talent/comment/${commentId}/`, data);
    }
}

export const eventApi = new EventApi();
